import React, { Component } from "react";
import PropTypes from "prop-types";
import { Dialog, Classes } from "@blueprintjs/core";
import { BaseButton, BUTTON_TYPE, SIZE } from "prefab";
import StatusIcon from "../StatusIcon";
import { CAMPAIGN_STATUS_LIST, STATUS_ICON_TYPES } from "../../../constants";
import ButtonStyles from "../../../styles/Button.module.scss";
import { WarningCircle } from "../../../assets";
import styles from "./DuplicateDataWarning.module.scss";

const INITIAL_VISIBLE_CAMPAIGNS = 2;

const getStatusColor = (status) => {
  const statusItem = CAMPAIGN_STATUS_LIST.find((item) => item.id === status);
  return statusItem ? statusItem.color : "";
};

class DuplicateDataWarning extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onIgnoreAndSave: PropTypes.func.isRequired,
    cplConflicts: PropTypes.shape({
      cplName: PropTypes.string,
      message: PropTypes.string,
      campaigns: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          code: PropTypes.string,
          name: PropTypes.string,
          media: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
              status: PropTypes.string,
            })
          ),
        })
      ),
    }),
    thirdpartyMediaIDConflicts: PropTypes.shape({
      thirdpartyMediaId: PropTypes.string,
      message: PropTypes.string,
      campaigns: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          code: PropTypes.string,
          name: PropTypes.string,
          media: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
              status: PropTypes.string,
            })
          ),
        })
      ),
    }),
  };

  state = {
    cplExpanded: false,
    tmidExpanded: false,
  };

  renderConflictTable = (campaigns, expanded, toggleKey) => {
    const visibleCampaigns = expanded ? campaigns : campaigns.slice(0, INITIAL_VISIBLE_CAMPAIGNS);
    const hiddenCount = campaigns.length - INITIAL_VISIBLE_CAMPAIGNS;

    return (
      <>
        <div className={styles.tableWrapper}>
          <table className={styles.conflictTable}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Media</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleCampaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    <a
                      className={styles.campaignLink}
                      href={`/campaigns/campaigns/${campaign.id}/details`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${campaign.code}: ${campaign.name}`}
                    >
                      {campaign.code}: {campaign.name}
                    </a>
                  </td>
                  <td>
                    {campaign.media.map((media, idx) => (
                      <div key={idx} className={styles.mediaItem}>
                        <a
                          className={styles.mediaLink}
                          href={`/campaigns/media/${media.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={media.name}
                        >
                          {(media?.id || "")?.substring(0, 8)}: {media.name}
                        </a>
                      </div>
                    ))}
                  </td>
                  <td>
                    {campaign.media.map((media, idx) => (
                      <div key={idx} className={styles.statusItem}>
                        <StatusIcon
                          color={getStatusColor(media.status)}
                          type={STATUS_ICON_TYPES.CIRCLE}
                        />
                        <span>{media.status}</span>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!expanded && hiddenCount > 0 && (
          <button
            className={styles.showMoreToggle}
            onClick={() => this.setState({ [toggleKey]: true })}
          >
            + {hiddenCount} more
          </button>
        )}
      </>
    );
  };

  renderCPLSection = () => {
    const { cplConflicts } = this.props;
    if (!cplConflicts) return null;

    const { campaigns, cplName } = cplConflicts;
    const count = campaigns.length;

    return (
      <div className={styles.conflictSection}>
        <div className={styles.conflictMessage}>
          CPL{" "}
          <span className={styles.boldText}>
            {cplName && cplName.length > 20 ? `${cplName.substring(0, 20)}...` : cplName}
          </span>{" "}
          is already used in <span className={styles.boldText}>{count}</span> other campaign
          {count > 1 ? "s" : ""} for the same screens and dates. Re-master content with new CPL name
          for easy reconciliation.
        </div>
        {this.renderConflictTable(campaigns, this.state.cplExpanded, "cplExpanded")}
      </div>
    );
  };

  renderThirdpartyMediaIDSection = () => {
    const { thirdpartyMediaIDConflicts } = this.props;
    if (!thirdpartyMediaIDConflicts) return null;

    const { campaigns, thirdpartyMediaId } = thirdpartyMediaIDConflicts;
    const count = campaigns.length;

    return (
      <div className={styles.conflictSection}>
        <div className={styles.conflictMessage}>
          Third Party Media ID <span className={styles.boldText}>{thirdpartyMediaId}</span> is
          already used in <span className={styles.boldText}>{count}</span> other campaign
          {count > 1 ? "s" : ""}.
        </div>
        {this.renderConflictTable(campaigns, this.state.tmidExpanded, "tmidExpanded")}
      </div>
    );
  };

  render() {
    const { isOpen, onClose, onIgnoreAndSave } = this.props;

    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        icon={<WarningCircle width={25} height={25} style={{ marginRight: 8 }} />}
        title="Duplicate Data Warning"
        isCloseButtonShown={true}
        className={styles.duplicateWarningDialog}
      >
        <div className={Classes.DIALOG_BODY}>
          {this.renderCPLSection()}
          {this.renderThirdpartyMediaIDSection()}
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <BaseButton
              onClick={onIgnoreAndSave}
              buttonText="Ignore & Save"
              buttonSize={SIZE.SMALL}
              className={ButtonStyles.buttonSpacing}
            />
            <BaseButton
              onClick={onClose}
              buttonText="Cancel"
              buttonSize={SIZE.SMALL}
              buttonType={BUTTON_TYPE.PRIMARY}
            />
          </div>
        </div>
      </Dialog>
    );
  }
}

export default DuplicateDataWarning;
